/** One row per outreach wave — status, progress and when its next email goes. Presentational. */
import { Fragment } from "react";

import type { Wave } from "../../api/b2g";
import { formatRelative } from "../../lib/formatRelative";
import { lettersToSignText, waveStatusTone } from "../../lib/b2g";
import { berlinDay, holdText, whenLabel, type WavePlan } from "../../lib/sendPlan";
import { Pill } from "../Pill";
import { ShortId } from "../ShortId";
import { CountryLabel } from "./CountryLabel";

export function WavesTable({
  waves,
  plan,
  horizonDays,
  onOpen,
}: {
  waves: Wave[];
  /** Per wave from the send plan; absent while it loads. */
  plan?: Map<string, WavePlan>;
  horizonDays: number;
  onOpen: (id: string) => void;
}) {
  const today = berlinDay(new Date());
  return (
    <div className="panel panel--flush overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>Wave</th>
            <th>Status</th>
            <th>Markets</th>
            <th className="num">People</th>
            <th className="num">Sent</th>
            <th className="num">Replied</th>
            <th className="num">Bounced</th>
            <th className="num">Pending</th>
            <th>Next send</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {waves.map((w) => (
            <tr key={w.id} className="cursor-pointer" onClick={() => onOpen(w.id)}>
              <td>
                <div className="font-heading font-semibold text-ink">{w.title}</div>
                <div className="text-xs text-ink-low">
                  <ShortId id={w.id} /> · {w.agent_slug ?? "seat"} · v{w.version}
                  {w.strategies?.length ? ` · ${w.strategies.join(", ")}` : ""}
                </div>
              </td>
              <td>
                <Pill value={w.status} tone={waveStatusTone(w.status)} />
                {w.progress && w.progress.checks_pending > 0 ? (
                  <div className="text-xs text-ink-low mt-1">{w.progress.checks_pending} checks running</div>
                ) : null}
                {w.progress?.letters_to_sign ? (
                  <div className="text-xs text-warn-fg mt-1 whitespace-nowrap">
                    {lettersToSignText(w.progress.letters_to_sign)}
                  </div>
                ) : null}
              </td>
              <td className="text-sm text-ink-medium">
                {w.markets?.length
                  ? w.markets.map((iso, i) => (
                      <Fragment key={iso}>
                        {i > 0 ? ", " : null}
                        <CountryLabel iso2={iso} />
                      </Fragment>
                    ))
                  : "–"}
              </td>
              <td className="num tnum">{w.progress?.recipients ?? "–"}</td>
              <td className="num tnum">{w.progress?.sent ?? "–"}</td>
              <td className="num tnum">{w.progress?.replied ?? "–"}</td>
              <td className="num tnum">{w.progress?.bounced ?? "–"}</td>
              <td className="num tnum">{w.progress?.pending ?? "–"}</td>
              <td className="text-sm whitespace-nowrap">
                <NextSend wave={plan?.get(w.id)} today={today} horizonDays={horizonDays} />
              </td>
              <td className="text-sm text-ink-low">{w.updated_at ? formatRelative(w.updated_at) : "–"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NextSend({ wave, today, horizonDays }: { wave?: WavePlan; today: string; horizonDays: number }) {
  if (wave?.next?.at) {
    const last = wave.last?.at && berlinDay(wave.last.at) !== berlinDay(wave.next.at) ? wave.last.at : null;
    return (
      <>
        <span className="tnum">≈ {whenLabel(wave.next.at, today)}</span>
        {last ? <div className="text-xs text-ink-low tnum">last ≈ {whenLabel(last, today)}</div> : null}
      </>
    );
  }
  if (wave?.held.length) return <span className="text-ink-low">{holdText(wave.held[0], horizonDays)}</span>;
  return <span className="text-ink-low">–</span>;
}
